import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { activityReportService } from '../../services/activityReportService';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import LoadingState from '../../components/ui/LoadingState';
import NavigationBar from '../../components/ui/NavigationBar';
import MobileTabBar from '../../components/ui/MobileTabBar';

const ActivityReportPage = () => {
  const navigate = useNavigate();
  const { user, profile, loading: authLoading } = useAuth();

  // Form state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reports, setReports] = useState([]);
  const [isLoadingReports, setIsLoadingReports] = useState(true);
  const [activeTab, setActiveTab] = useState('submit'); // submit | history
  const [expandedReportId, setExpandedReportId] = useState(null);
  const [printingReportId, setPrintingReportId] = useState(null);

  // Form fields
  const [formData, setFormData] = useState({
    weekStarting: getThisWeekMonday(),
    callsCompleted: 0,
    demosCompleted: 0,
    pitchesCompleted: 0,
    keyWins: '',
    challengesFaced: '',
    objectionsFaced: [],
    techniquesApplied: [],
    rawNotes: ''
  });

  const [objectionInput, setObjectionInput] = useState('');
  const [techniqueInput, setTechniqueInput] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }

    loadReports();
  }, [user, authLoading, navigate]);

  function getThisWeekMonday() {
    const d = new Date();
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(d.setDate(diff));
    return monday.toISOString().split('T')[0];
  }

  async function loadReports() {
    setIsLoadingReports(true);
    try {
      const data = await activityReportService.getActivityReports(user.id, 10);
      setReports(data);
    } catch (error) {
      console.error('Error loading reports:', error);
    } finally {
      setIsLoadingReports(false);
    }
  }

  function handleAddObjection() {
    if (objectionInput.trim()) {
      setFormData(prev => ({
        ...prev,
        objectionsFaced: [...prev.objectionsFaced, objectionInput.trim()]
      }));
      setObjectionInput('');
    }
  }

  function handleRemoveObjection(index) {
    setFormData(prev => ({
      ...prev,
      objectionsFaced: prev.objectionsFaced.filter((_, i) => i !== index)
    }));
  }

  function handleAddTechnique() {
    if (techniqueInput.trim()) {
      setFormData(prev => ({
        ...prev,
        techniquesApplied: [...prev.techniquesApplied, techniqueInput.trim()]
      }));
      setTechniqueInput('');
    }
  }

  function handleRemoveTechnique(index) {
    setFormData(prev => ({
      ...prev,
      techniquesApplied: prev.techniquesApplied.filter((_, i) => i !== index)
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await activityReportService.submitActivityReport(user.id, formData);

      // Reset form
      setFormData({
        weekStarting: getThisWeekMonday(),
        callsCompleted: 0,
        demosCompleted: 0,
        pitchesCompleted: 0,
        keyWins: '',
        challengesFaced: '',
        objectionsFaced: [],
        techniquesApplied: [],
        rawNotes: ''
      });

      // Reload reports
      await loadReports();
      setActiveTab('history');

      // Show success message
      alert('✓ Activity report submitted! Lumo is analyzing your week...');
    } catch (error) {
      console.error('Error submitting report:', error);
      alert('Error submitting report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handlePrintReport(report) {
    setPrintingReportId(report.id);
    try {
      const html = activityReportService.generateHTMLReport(report);
      const printWindow = window.open('', '_blank');
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.print();
    } catch (error) {
      console.error('Error printing report:', error);
      alert('Error generating print report.');
    } finally {
      setPrintingReportId(null);
    }
  }

  if (authLoading) {
    return <LoadingState />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <NavigationBar />

      <div className="max-w-4xl mx-auto px-4 py-8 mb-24 md:mb-0">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-foreground mb-2">Weekly Activity Report</h1>
          <p className="text-lg text-foreground/70">
            Submit your weekly sales activity and receive personalized Lumo coaching feedback
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-4 mb-8 border-b border-border">
          <button
            onClick={() => setActiveTab('submit')}
            className={`px-6 py-4 font-semibold transition ${
              activeTab === 'submit'
                ? 'text-primary border-b-2 border-primary'
                : 'text-foreground/60 hover:text-foreground'
            }`}
          >
            Submit Activity
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-6 py-4 font-semibold transition ${
              activeTab === 'history'
                ? 'text-primary border-b-2 border-primary'
                : 'text-foreground/60 hover:text-foreground'
            }`}
          >
            Past Reports ({reports.length})
          </button>
        </div>

        {/* Submit Form */}
        {activeTab === 'submit' && (
          <div className="bg-card border border-border rounded-xl p-8 shadow-lg">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Week Selection */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-foreground">
                  Week Starting
                </label>
                <Input
                  type="date"
                  value={formData.weekStarting}
                  onChange={e => setFormData({ ...formData, weekStarting: e.target.value })}
                  className="w-full"
                />
              </div>

              {/* Activity Counts */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-foreground">Activity Summary</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-foreground/80">
                      Calls Completed
                    </label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.callsCompleted}
                      onChange={e =>
                        setFormData({ ...formData, callsCompleted: parseInt(e.target.value) || 0 })
                      }
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-foreground/80">
                      Demos Completed
                    </label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.demosCompleted}
                      onChange={e =>
                        setFormData({ ...formData, demosCompleted: parseInt(e.target.value) || 0 })
                      }
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-foreground/80">
                      Pitches Completed
                    </label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.pitchesCompleted}
                      onChange={e =>
                        setFormData({ ...formData, pitchesCompleted: parseInt(e.target.value) || 0 })
                      }
                      className="w-full"
                    />
                  </div>
                </div>
              </div>

              {/* Key Wins */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-foreground">
                  🎯 Key Wins This Week
                </label>
                <textarea
                  value={formData.keyWins}
                  onChange={e => setFormData({ ...formData, keyWins: e.target.value })}
                  placeholder="Describe your biggest wins, closed deals, or breakthrough moments..."
                  className="w-full h-24 px-4 py-3 rounded-lg border border-border bg-background text-foreground placeholder-foreground/40 focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              {/* Challenges */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-foreground">
                  ⚠️ Challenges Faced
                </label>
                <textarea
                  value={formData.challengesFaced}
                  onChange={e => setFormData({ ...formData, challengesFaced: e.target.value })}
                  placeholder="What difficulties did you encounter? How did you overcome them?"
                  className="w-full h-24 px-4 py-3 rounded-lg border border-border bg-background text-foreground placeholder-foreground/40 focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              {/* Objections Faced */}
              <div className="space-y-3">
                <label className="block text-sm font-semibold text-foreground">
                  💬 Objections You Faced
                </label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={objectionInput}
                    onChange={e => setObjectionInput(e.target.value)}
                    onKeyPress={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddObjection();
                      }
                    }}
                    placeholder="Type objection and press Enter..."
                    className="flex-1"
                  />
                  <Button
                    onClick={handleAddObjection}
                    variant="outline"
                    type="button"
                  >
                    Add
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.objectionsFaced.map((objection, i) => (
                    <div
                      key={i}
                      className="px-4 py-2 bg-primary/10 border border-primary/30 rounded-full text-sm text-primary flex items-center gap-2"
                    >
                      {objection}
                      <button
                        type="button"
                        onClick={() => handleRemoveObjection(i)}
                        className="text-primary/60 hover:text-primary font-bold"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Techniques Applied */}
              <div className="space-y-3">
                <label className="block text-sm font-semibold text-foreground">
                  ✨ Techniques You Applied
                </label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={techniqueInput}
                    onChange={e => setTechniqueInput(e.target.value)}
                    onKeyPress={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddTechnique();
                      }
                    }}
                    placeholder="Type technique (e.g., SPIN Questioning, Mirror & Pivot) and press Enter..."
                    className="flex-1"
                  />
                  <Button
                    onClick={handleAddTechnique}
                    variant="outline"
                    type="button"
                  >
                    Add
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.techniquesApplied.map((technique, i) => (
                    <div
                      key={i}
                      className="px-4 py-2 bg-success/10 border border-success/30 rounded-full text-sm text-success flex items-center gap-2"
                    >
                      {technique}
                      <button
                        type="button"
                        onClick={() => handleRemoveTechnique(i)}
                        className="text-success/60 hover:text-success font-bold"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Additional Notes */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-foreground">
                  📝 Additional Notes
                </label>
                <textarea
                  value={formData.rawNotes}
                  onChange={e => setFormData({ ...formData, rawNotes: e.target.value })}
                  placeholder="Any other insights or details you'd like Lumo to consider..."
                  className="w-full h-20 px-4 py-3 rounded-lg border border-border bg-background text-foreground placeholder-foreground/40 focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              {/* Submit Button */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Activity Report'}
                </Button>
              </div>

              <p className="text-xs text-foreground/60 text-center">
                ✓ Your activity will be analyzed by Lumo and you'll receive personalized coaching feedback
              </p>
            </form>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="space-y-6">
            {isLoadingReports ? (
              <LoadingState />
            ) : reports.length === 0 ? (
              <div className="bg-card border border-border rounded-xl p-12 text-center">
                <p className="text-lg text-foreground/60">No activity reports yet</p>
                <p className="text-sm text-foreground/40 mt-2">Submit your first weekly activity to get started</p>
              </div>
            ) : (
              reports.map(report => (
                <div
                  key={report.id}
                  className="bg-card border border-border rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition"
                >
                  {/* Header */}
                  <div
                    className="p-6 border-b border-border cursor-pointer hover:bg-background/50 transition"
                    onClick={() =>
                      setExpandedReportId(expandedReportId === report.id ? null : report.id)
                    }
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-foreground">
                          Week of {new Date(report.week_starting).toLocaleDateString()}
                        </h3>
                        <p className="text-sm text-foreground/60 mt-1">
                          {report.total_activities} activities • {report.is_analyzed ? '✓ Analyzed' : '⏳ Pending'}
                        </p>
                      </div>
                      {report.mentor_feedback && (
                        <div className="text-right">
                          <div
                            className={`text-3xl font-bold ${
                              report.mentor_feedback.score >= 80
                                ? 'text-success'
                                : report.mentor_feedback.score >= 70
                                  ? 'text-warning'
                                  : 'text-error'
                            }`}
                          >
                            {report.mentor_feedback.score}
                          </div>
                          <p className="text-xs text-foreground/60">Mentor Score</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {expandedReportId === report.id && (
                    <div className="p-6 space-y-6 bg-background/30">
                      {/* Activity Summary */}
                      <div className="grid grid-cols-3 gap-4">
                        <div className="bg-primary/10 p-4 rounded-lg text-center">
                          <div className="text-2xl font-bold text-primary">{report.calls_completed}</div>
                          <p className="text-sm text-foreground/60 mt-1">Calls</p>
                        </div>
                        <div className="bg-primary/10 p-4 rounded-lg text-center">
                          <div className="text-2xl font-bold text-primary">{report.demos_completed}</div>
                          <p className="text-sm text-foreground/60 mt-1">Demos</p>
                        </div>
                        <div className="bg-primary/10 p-4 rounded-lg text-center">
                          <div className="text-2xl font-bold text-primary">{report.pitches_completed}</div>
                          <p className="text-sm text-foreground/60 mt-1">Pitches</p>
                        </div>
                      </div>

                      {/* Key Wins */}
                      {report.key_wins && (
                        <div>
                          <h4 className="font-semibold text-foreground mb-2">🎯 Key Wins</h4>
                          <p className="text-foreground/80">{report.key_wins}</p>
                        </div>
                      )}

                      {/* Mentor Feedback */}
                      {report.mentor_feedback && (
                        <div className="space-y-4 border-t border-border pt-4">
                          <div className="bg-success/10 border-l-4 border-success p-4 rounded">
                            <h4 className="font-semibold text-success mb-2">✓ What You Did Well</h4>
                            <p className="text-foreground/80">{report.mentor_feedback.strengths || 'Keep up the great work!'}</p>
                          </div>

                          <div className="bg-blue-500/10 border-l-4 border-blue-500 p-4 rounded">
                            <h4 className="font-semibold text-blue-500 mb-2">💡 Pattern Analysis</h4>
                            <p className="text-foreground/80">{report.mentor_feedback.patterns || 'Continue building on your strengths.'}</p>
                          </div>

                          <div className="bg-indigo-500/10 border-l-4 border-indigo-500 p-4 rounded">
                            <h4 className="font-semibold text-indigo-500 mb-2">🎯 Industry Tips</h4>
                            <p className="text-foreground/80">{report.mentor_feedback.industryTips || 'Apply your skills consistently.'}</p>
                          </div>

                          <div className="bg-primary/10 border-l-4 border-primary p-4 rounded">
                            <h4 className="font-semibold text-primary mb-2">🔧 Focus Technique</h4>
                            <p className="text-foreground/80">{report.mentor_feedback.focusTechnique || 'Master one technique this week.'}</p>
                          </div>

                          <div className="bg-warning/10 border-l-4 border-warning p-4 rounded">
                            <h4 className="font-semibold text-warning mb-2">🔥 Next Week's Goal</h4>
                            <p className="text-foreground/80">{report.mentor_feedback.nextWeekGoal || 'Set your goal for next week.'}</p>
                          </div>

                          {report.mentor_feedback.practiceLinks?.length > 0 && (
                            <div className="bg-accent/10 border-l-4 border-accent p-4 rounded">
                              <h4 className="font-semibold text-accent mb-2">🚀 Quick Actions</h4>
                              <div className="flex flex-col gap-2">
                                {report.mentor_feedback.practiceLinks.map((link, idx) => (
                                  <Button
                                    key={idx}
                                    variant="ghost"
                                    className="justify-start text-primary font-semibold hover:underline"
                                    onClick={() => navigate(link?.url || '/app/techniques-library')}
                                  >
                                    {link?.label || 'Open training resource'}
                                  </Button>
                                ))}
                              </div>
                            </div>
                          )}

                          <div className="italic text-foreground/70 p-4 bg-foreground/5 rounded">
                            💪 "{report.mentor_feedback.encouragement || 'You\'re making progress. Keep going!'}"
                          </div>

                          {report.mentor_feedback.score && (
                            <div className="bg-primary/10 p-4 rounded-lg text-center border border-primary/30">
                              <p className="text-sm text-foreground/60 mb-2">This Week's Mentor Score</p>
                              <div className={`text-4xl font-bold ${
                                report.mentor_feedback.score >= 80
                                  ? 'text-success'
                                  : report.mentor_feedback.score >= 70
                                    ? 'text-warning'
                                    : 'text-error'
                              }`}>
                                {report.mentor_feedback.score}/100
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {!report.is_analyzed && (
                        <div className="bg-warning/10 border border-warning/30 p-4 rounded text-center">
                          <p className="text-sm text-warning">
                            ⏳ Lumo is analyzing your activity. Check back soon for personalized feedback.
                          </p>
                        </div>
                      )}

                      {/* Print Button */}
                      <Button
                        onClick={() => handlePrintReport(report)}
                        disabled={printingReportId === report.id}
                        variant="outline"
                        className="w-full"
                      >
                        {printingReportId === report.id ? 'Preparing...' : '🖨️ Print Report'}
                      </Button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      <MobileTabBar />
    </div>
  );
};

export default ActivityReportPage;
